import { useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useBorrowerBaseballCardStore } from '@roc/feature-borrowers';
// Raw paths (not @roc/ aliases) are intentional — aliases trip Nx's circular-dependency boundary rule.
import {
  estimatedFFExperienceOptions,
  estimatedGUExperienceOptions,
  estimatedRentalExperienceOptions,
} from 'libs/feature-borrower-review/src/components/newBorrowerExperienceData';
import EditBorrowerExperienceModal from 'libs/ui/src/lib/components/borrowerBaseballCard/editBorrowerExperienceModal';
import { useCenteredDialogStyles } from './centeredDialogStyles';
import { QuestionContent } from './questionContent';
import { PropertyMethodPicker } from './propertyMethodPicker';
import { UploadedTrackRecordFile } from './uploadedTrackRecordFile';
import { AgentRunsCard } from './inProgressTable';
import { AnswerValue, QUESTIONS, getExperienceLabel } from './trackRecordV2.config';
import { getPropertiesStatusCounts } from './propertiesTab';
import { buildLicenseRows, downloadLicenseDocument } from './licensesTab';

export const OverviewTab = observer(({
  classes,
  answers,
  onAnswer,
  verificationStore,
  documentStore,
  borrowerId,
  isInternal,
  onNavigate,
}: {
  classes: Record<string, string>;
  answers: Record<string, AnswerValue>;
  onAnswer: (key: string, value: AnswerValue) => void;
  verificationStore?: any;
  documentStore?: any;
  borrowerId?: number | string;
  isInternal?: boolean;
  onNavigate?: (item: 'properties' | 'entities') => void;
}) => {
  const { borrowerBaseballCardStore } = useBorrowerBaseballCardStore();
  const centeredDialogClasses = useCenteredDialogStyles();
  // Hide the Edit-experience flow outside the internal store where saveExperienceComment would no-op.
  const canManage = !!isInternal && !!borrowerBaseballCardStore?.isTrackRecordVerificationEnabled;
  const [editExperienceOpen, setEditExperienceOpen] = useState(false);

  const overviewSummary = verificationStore?.trackRecordOverview?.summary ?? {};

  const propertiesCounts = getPropertiesStatusCounts(verificationStore);
  const propertiesTotal = verificationStore?.properties?.length ?? 0;
  const entitiesTotal = verificationStore?.entities?.length ?? 0;
  // Same rows LicensesTab renders — lets a claimed license's uploaded file be
  // downloaded straight from here instead of only from the Licenses tab.
  const licenseRows = buildLicenseRows(verificationStore);
  const handleDownloadLicense = (document: any, taskId: any) =>
    downloadLicenseDocument(documentStore, verificationStore, document, taskId);

  const verifiedProperties = (verificationStore?.properties ?? []).filter((property: any) => property.isVerified);
  const fixAndFlipVerifiedCount = verifiedProperties.filter((property: any) => property.bridge).length;
  const groundUpVerifiedCount = verifiedProperties.filter((property: any) => property.groundUp).length;
  const termVerifiedCount = verifiedProperties.filter((property: any) => property.term).length;

  // Not part of the overview payload — fetch lazily only when Edit opens.
  const handleOpenEditExperience = () => {
    const bId = borrowerId ?? verificationStore?.borrowerInfo?.borrowerId;
    if (bId) {
      borrowerBaseballCardStore.setCurrentBorrower(bId);
      borrowerBaseballCardStore.fetchBaseballCardData(bId);
    }
    setEditExperienceOpen(true);
  };

  const handleExperienceSave = async () => {
    const experienceBorrowerId = borrowerBaseballCardStore.currentBorrower ?? verificationStore?.borrowerInfo?.borrowerId;
    try {
      const request = {
        comment: borrowerBaseballCardStore.baseballCardDetails?.experienceComment,
        rentalExp: borrowerBaseballCardStore.baseballCardDetails?.rentalExp,
        bridgeExp: borrowerBaseballCardStore.baseballCardDetails?.bridgeExp,
        rentalMfExperience: borrowerBaseballCardStore.baseballCardDetails?.rentalMfExperience,
        groundupExperience: borrowerBaseballCardStore.baseballCardDetails?.groundupExperience,
        totalPropertiesOwned: borrowerBaseballCardStore.baseballCardDetails?.totalPropertiesOwned,
      };
      await borrowerBaseballCardStore.saveExperienceComment(experienceBorrowerId, request);
      borrowerBaseballCardStore.showSuccessNotification?.('Experience saved successfully');
      if (experienceBorrowerId) {
        await borrowerBaseballCardStore.fetchBaseballCardData(experienceBorrowerId);
      }
    } catch (error) {
      borrowerBaseballCardStore.showErrorNotification?.('Error while saving experience');
    } finally {
      setEditExperienceOpen(false);
    }
  };

  return (
    <>
      <Typography variant="h5" className={classes.overviewTitle}>
        Overview
      </Typography>
      <Typography className={classes.overviewSubtitle}>
        Borrower reported {propertiesTotal} properties across {entitiesTotal} entities. {propertiesCounts.verified} verified
      </Typography>

      <Box className={classes.card} style={{ marginBottom: 16 }}>
        <Typography className={classes.cardHeaderTitle} style={{ marginBottom: 16 }}>
          Submitted Summary
        </Typography>
        <Box className={classes.summaryRow}>
          <Box>
            {verificationStore?.uploadedTrackRecordFile ? (
              <Box
                className={classes.uploadLink}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                onClick={() =>
                  handleDownloadLicense(
                    { original_file_name: verificationStore.uploadedTrackRecordFile.originalFileName },
                    verificationStore.uploadedTrackRecordFile.taskId,
                  )
                }
              >
                <GetAppIcon style={{ fontSize: 14 }} />
                <Typography className={classes.summaryStatTitle} style={{ color: 'inherit' }} noWrap>
                  {verificationStore.uploadedTrackRecordFile.originalFileName}
                </Typography>
              </Box>
            ) : (
              <Typography className={classes.summaryStatTitle}>—</Typography>
            )}
            <Typography className={classes.summaryStatCaption}>Excel Template</Typography>
          </Box>
          <Box>
            <Box className={classes.summaryStatBigRow}>
              <Typography className={classes.summaryStatBig}>{propertiesTotal}</Typography>
              <Typography className={classes.summaryStatBigLabel}>Unique</Typography>
            </Box>
            <Typography className={classes.summaryStatCaption}>Properties</Typography>
          </Box>
          <Box>
            <Box className={classes.summaryStatBigRow}>
              <Typography className={classes.summaryStatBig}>{entitiesTotal}</Typography>
              <Typography className={classes.summaryStatBigLabel}>LLCs</Typography>
            </Box>
            <Typography className={classes.summaryStatCaption}>Entities Claimed</Typography>
          </Box>
          <Box>
            {licenseRows.filter((row) => row.claimed).length > 0 ? (
              licenseRows.filter((row) => row.claimed).map((row) =>
                row.document ? (
                  <Box
                    key={row.key}
                    className={classes.uploadLink}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                    onClick={() => handleDownloadLicense(row.document, row.taskId)}
                  >
                    <GetAppIcon style={{ fontSize: 14 }} />
                    <Typography className={classes.summaryStatTitle} style={{ color: 'inherit' }} noWrap>
                      {row.document.original_file_name}
                    </Typography>
                  </Box>
                ) : (
                  <Typography key={row.key} className={classes.summaryStatTitle}>{row.name}</Typography>
                )
              )
            ) : (
              <Typography className={classes.summaryStatTitle}>—</Typography>
            )}
            <Typography className={classes.summaryStatCaption}>License Claimed</Typography>
          </Box>
          <Box className={classes.summaryVerifiedBadgeCell}>
            <Box className={classes.verifiedBadge}>Verified</Box>
          </Box>
        </Box>
      </Box>

      <Box className={classes.card} style={{ marginBottom: 16 }}>
        <Box className={classes.cardHeader}>
          <Typography className={classes.cardHeaderTitle}>Borrower Experience</Typography>
          {canManage && !overviewSummary.locked_data && (
            <span className={classes.editLink} onClick={handleOpenEditExperience}>
              <EditOutlinedIcon style={{ fontSize: 14 }} />
              Edit
            </span>
          )}
        </Box>
        <Box className={classes.experienceRow}>
          <Box>
            <Typography className={classes.experienceLabel}>Fix and Flip / Stabilized Bridge</Typography>
            <Typography className={classes.experienceValue}>
              {getExperienceLabel(estimatedFFExperienceOptions, overviewSummary.bridge_exp)}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.experienceLabel}>Ground Up Construction</Typography>
            <Typography className={classes.experienceValue}>
              {getExperienceLabel(estimatedGUExperienceOptions, overviewSummary.groundup_exp)}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.experienceLabel}>Term</Typography>
            <Typography className={classes.experienceValue}>
              {getExperienceLabel(estimatedRentalExperienceOptions, overviewSummary.rental_exp)}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.experienceLabel}>Total Properties Verified / Submitted</Typography>
            <Typography className={classes.experienceValue}>
              {propertiesCounts.verified} / {propertiesTotal}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.experienceRow} style={{ marginBottom: 0 }}>
          <Box>
            <Typography className={classes.experienceLabel}>Fix and Flip Verified</Typography>
            <Typography className={classes.experienceValue}>{fixAndFlipVerifiedCount}</Typography>
          </Box>
          <Box>
            <Typography className={classes.experienceLabel}>Ground Up Verified</Typography>
            <Typography className={classes.experienceValue}>{groundUpVerifiedCount}</Typography>
          </Box>
          <Box>
            <Typography className={classes.experienceLabel}>Term Verified</Typography>
            <Typography className={classes.experienceValue}>{termVerifiedCount}</Typography>
          </Box>
        </Box>
        {borrowerBaseballCardStore.baseballCardDetails?.experienceComment && (
          <Box style={{ marginTop: 16 }}>
            <Typography className={classes.experienceLabel}>Comments</Typography>
            <Typography className={classes.experienceValue}>
              {borrowerBaseballCardStore.baseballCardDetails.experienceComment}
            </Typography>
          </Box>
        )}
      </Box>

      {canManage && (
        <EditBorrowerExperienceModal
          dialogClasses={centeredDialogClasses}
          open={editExperienceOpen}
          handleClose={() => setEditExperienceOpen(false)}
          store={borrowerBaseballCardStore}
          handleSave={handleExperienceSave}
        />
      )}

      <AgentRunsCard classes={classes} verificationStore={verificationStore} borrowerId={borrowerId} />

      <Box className={classes.card} style={{ marginBottom: 16 }}>
        <Typography className={classes.cardHeaderTitle} style={{ marginBottom: 16 }}>
          Eligibility
        </Typography>
        <Box className={classes.eligibilityGrid}>
          <Box className={classes.questionCardNoMargin}>
            <QuestionContent
              question={QUESTIONS[0]}
              answers={answers}
              onAnswer={onAnswer}
              classes={classes}
              verificationStore={verificationStore}
              documentStore={documentStore}
              borrowerId={borrowerId}
            />
          </Box>
          <Box className={classes.questionCardNoMargin}>
            <QuestionContent
              question={QUESTIONS[1]}
              answers={answers}
              onAnswer={onAnswer}
              classes={classes}
              verificationStore={verificationStore}
              documentStore={documentStore}
              borrowerId={borrowerId}
            />
          </Box>
          <Box className={`${classes.questionCardNoMargin} ${classes.eligibilityGridFullRow}`}>
            <QuestionContent
              question={QUESTIONS[2]}
              answers={answers}
              onAnswer={onAnswer}
              classes={classes}
              verificationStore={verificationStore}
              documentStore={documentStore}
              borrowerId={borrowerId}
            />
          </Box>
        </Box>
      </Box>

      {answers.previousExperience === 'Yes' && (
        <Box className={classes.card} style={{ marginBottom: 16 }}>
          <Typography className={classes.cardHeaderTitle} style={{ marginBottom: 4 }}>
            Add Records
          </Typography>
          <Typography className={classes.overviewSubtitle}>
            Upload a spreadsheet for the fastest path, or look up records we already hold.
          </Typography>
          <UploadedTrackRecordFile classes={classes} verificationStore={verificationStore} documentStore={documentStore} />
          <PropertyMethodPicker
            classes={classes}
            showRecommended
            showTemplateLink
            verificationStore={verificationStore}
            borrowerId={borrowerId}
            onNavigate={onNavigate}
          />
        </Box>
      )}
    </>
  );
});
